import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-x-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive: "hover:bg-rw-red/90 bg-rw-red text-white",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        teal: "bg-rw-main text-white hover:bg-rw-main-hover",
        submit:
          "bg-rw-sub-deep text-white hover:bg-rw-sub-deeper disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500",
        eventLoungeWideActive: "bg-rw-event-lounge hover:opacity-80",
        eventLoungeWideInactive:
          "border border-dashed border-rw-amber-200 bg-rw-amber-50 text-rw-amber-300 hover:opacity-80",
        eventStudyGroupOnlyActive:
          "bg-rw-event-blue text-white hover:opacity-80",
        eventStudyGroupOnlyInactive:
          "border border-dashed border-rw-blue-200 bg-rw-blue-50 text-rw-blue-300 hover:opacity-80",
        eventAttending: "bg-rw-main text-white hover:opacity-80",
        eventInterested:
          "border border-amber-200 bg-amber-100 text-amber-900 hover:opacity-80",
        eventInvited:
          "border border-violet-200 bg-violet-50 text-violet-700 hover:opacity-80",
        eventDeclined:
          "border border-gray-200 bg-gray-100 text-gray-500 hover:opacity-80",
        eventNone:
          "border border-dashed border-gray-200 bg-gray-50 text-gray-500 hover:opacity-80",
        formattingActive:
          "bg-rw-sub-deep text-white shadow-sm hover:bg-rw-sub-deeper",
        formattingInactive:
          "text-rw-dark-gray hover:bg-gray-100 hover:text-rw-sub-deep",
        listDefault:
          "text-variable-collection-dark-gray hover:bg-rw-hover-gray",
        linkListButtons:
          "opacity-10 transition-opacity duration-150 hover:opacity-100",
        iconOutline: "border bg-white opacity-100",
        "rw-sub": "bg-rw-sub text-white",
        "rw-main": "bg-rw-main text-white",
        "rw-off-white": "bg-rw-white text-rw-black",
      },
      size: {
        default: "h-10 px-4 py-2",
        xs: "h-5 rounded-md px-1",
        sm: "h-8 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        xl: "h-12 rounded-md px-10",
        icon: "size-10",
        mini: "size-5",
        event: "h-auto px-1 py-0.5 text-xs",
        formatting: "h-auto px-2 py-1 text-lg",
      },
      iconSize: {
        sm: "[&_svg]:size-3",
        default: "[&_svg]:size-4",
        lg: "[&_svg]:size-5",
        xl: "[&_svg]:size-6",
      },
      shaped: {
        true: "rounded-none rounded-tr-lg",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      iconSize: "default",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
  loadingText?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      iconSize,
      shaped,
      asChild = false,
      isLoading = false,
      loadingText,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, iconSize, shaped, className }),
        )}
        ref={ref}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading && !asChild ? (
          <>
            <Loader2 className="size-4 animate-spin" />
            {loadingText ?? children}
          </>
        ) : (
          children
        )}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
